import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  Globe,
  Home as HomeIcon,
  Layers,
  Package,
  Wrench,
  Info,
  Mail,
  Sun,
  Moon,
  Laptop,
} from "lucide-react";
import { Logo } from "./Logo";
import { useLanguage } from "../i18n/LanguageContext";
import { useTheme } from "../theme/ThemeContext";

const navItems = (t) => [
  { to: "/", label: t.nav.home, id: "home", icon: HomeIcon },
  { to: "/services", label: t.nav.services, id: "services", icon: Layers },
  { to: "/offers", label: t.nav.offers, id: "offers", icon: Package },
  { to: "/tools", label: t.nav.tools, id: "tools", icon: Wrench },
  { to: "/about", label: t.nav.about, id: "about", icon: Info },
  { to: "/contact", label: t.nav.contact, id: "contact", icon: Mail },
];

const ThemeToggle = () => {
  const { mode, setMode } = useTheme();
  const options = [
    { id: "light", icon: Sun, label: "Light" },
    { id: "auto", icon: Laptop, label: "Auto" },
    { id: "dark", icon: Moon, label: "Dark" },
  ];
  return (
    <div
      className="hidden sm:flex items-center gap-1 px-1 py-1 rounded-full border border-white/10"
      data-testid="theme-switcher"
      role="radiogroup"
      aria-label="Theme"
    >
      {options.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          onClick={() => setMode(id)}
          data-testid={`theme-${id}`}
          title={label}
          aria-label={label}
          aria-pressed={mode === id}
          className={`p-1.5 rounded-full transition flex items-center justify-center ${
            mode === id
              ? "smk-gradient-bg text-black"
              : "text-zinc-400 hover:text-white"
          }`}
        >
          <Icon size={13} />
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t, lang, setLang } = useLanguage();
  const { mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 smk-glass"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <Logo withText size={32} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {navItems(t).map(({ to, label, id, icon: Icon }) => (
            <NavLink
              key={id}
              to={to}
              end={to === "/"}
              data-testid={`nav-${id}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                  isActive
                    ? "text-white bg-white/5"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon size={14} className="opacity-80" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle />

          <div
            className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-full border border-white/10"
            data-testid="lang-switcher"
          >
            <Globe size={14} className="text-zinc-500 ml-1" />
            <button
              onClick={() => setLang("fr")}
              data-testid="lang-fr"
              className={`px-2 py-0.5 text-xs rounded-full transition ${
                lang === "fr"
                  ? "smk-gradient-bg text-black font-semibold"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              FR
            </button>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-2 py-0.5 text-xs rounded-full transition ${
                lang === "en"
                  ? "smk-gradient-bg text-black font-semibold"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              EN
            </button>
          </div>

          <Link
            to="/contact"
            data-testid="header-cta"
            className="hidden md:inline-flex items-center px-4 py-2 rounded-full smk-gradient-bg text-black font-semibold text-sm hover:brightness-110 transition"
          >
            {t.nav.cta}
          </Link>

          <button
            className="lg:hidden p-2 text-zinc-300"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/80 backdrop-blur-xl" data-testid="mobile-nav">
          <nav className="px-6 py-4 flex flex-col gap-1">
            {navItems(t).map(({ to, label, id, icon: Icon }) => (
              <NavLink
                key={id}
                to={to}
                end={to === "/"}
                data-testid={`mobile-nav-${id}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                    isActive ? "text-white bg-white/5" : "text-zinc-400"
                  }`
                }
              >
                <Icon size={15} className="opacity-80" />
                <span>{label}</span>
              </NavLink>
            ))}
            <div className="flex items-center gap-2 mt-3 px-3 flex-wrap">
              <button
                onClick={() => setLang("fr")}
                data-testid="mobile-lang-fr"
                className={`px-3 py-1 text-xs rounded-full border ${
                  lang === "fr" ? "smk-gradient-bg text-black border-transparent" : "border-white/10 text-zinc-400"
                }`}
              >
                FR
              </button>
              <button
                onClick={() => setLang("en")}
                data-testid="mobile-lang-en"
                className={`px-3 py-1 text-xs rounded-full border ${
                  lang === "en" ? "smk-gradient-bg text-black border-transparent" : "border-white/10 text-zinc-400"
                }`}
              >
                EN
              </button>
              <div className="h-5 w-px bg-white/10 mx-1" />
              {[
                { id: "light", icon: Sun },
                { id: "auto", icon: Laptop },
                { id: "dark", icon: Moon },
              ].map(({ id, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setMode(id)}
                  data-testid={`mobile-theme-${id}`}
                  aria-label={`Theme ${id}`}
                  className={`p-1.5 rounded-full transition ${
                    mode === id
                      ? "smk-gradient-bg text-black"
                      : "border border-white/10 text-zinc-400"
                  }`}
                >
                  <Icon size={13} />
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
